import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Loader2, ArrowRight, ShieldCheck, TrendingUp, Wallet } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-primary p-12 text-primary-foreground relative overflow-hidden">
        <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-white/10" />
        <div className="absolute -left-16 bottom-10 h-56 w-56 rounded-full bg-white/5" />
        <div className="flex items-center gap-3 relative">
          <div className="h-14 w-14 rounded-2xl bg-white flex items-center justify-center overflow-hidden shadow-lg">
            <img src="/logo-gestion.jpeg" alt="Gestion" className="h-full w-full object-contain" />
          </div>
          <div>
            <p className="font-heading font-extrabold text-lg">General Food</p>
            <p className="text-sm text-primary-foreground/70">Gestion ERP</p>
          </div>
        </div>
        <div className="relative">
          <h2 className="font-heading text-4xl font-extrabold leading-tight tracking-tight">
            Pilotez vos dépenses,<br />factures et trésorerie<br />en temps réel.
          </h2>
          <p className="text-primary-foreground/80 mt-4 max-w-md">
            Une base de données unique pour vos sociétés : caisse, banque, fournisseurs et rapports automatisés.
          </p>
          <div className="flex gap-6 mt-10">
            {[
              [TrendingUp, "Tableau de bord"],
              [Wallet, "Caisse & Banque"],
              [ShieldCheck, "Traçabilité"],
            ].map(([Icon, label]) => (
              <div key={label} className="flex items-center gap-2 text-sm text-primary-foreground/90">
                <Icon className="h-4 w-4" /> {label}
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs text-primary-foreground/50 relative">Version 1.0 · 2026</p>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <img src="/logo-gestion.jpeg" alt="Gestion" className="h-10 w-10 rounded-lg object-contain" />
            <p className="font-heading font-extrabold text-lg">General Food</p>
          </div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Connexion</h1>
          <p className="text-muted-foreground text-sm mt-1 mb-8">
            Accédez à votre espace de gestion.
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Adresse e-mail</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="vous@entreprise.dz"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>
            {error && (
              <p data-testid="login-error" className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">
                {error}
              </p>
            )}
            <Button type="submit" data-testid="login-submit" className="w-full gap-2" disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Se connecter <ArrowRight className="h-4 w-4" /></>}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
